/**
 * Rendu du contenu d'une face de carte (design system, ContenuDeCarte) : Markdown et
 * extensions (src/contenu/syntaxe.ts) en éléments React. Le HTML écrit dans une carte n'est
 * jamais interprété : il s'affiche comme du texte.
 */
import type { SyntaxNode } from '@lezer/common';
import katex from 'katex';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { analyseurCartes, analyseurSansTrous } from '../../contenu/syntaxe';
import { BlocDeCode } from './BlocDeCode';

/** Trou demandé par la carte : son numéro, ou tous en mode « tout masquer ». */
export interface TrousDeLaCarte {
  face: 'recto' | 'verso';
  demande: number | 'tous';
}

/** Adresse affichable d'une image enregistrée (seau privé) : fournie par l'application. */
export type ResoudreImage = (chemin: string) => Promise<string>;

export const ContexteImages = createContext<ResoudreImage>(async (chemin) => chemin);
export const FournisseurImages = ContexteImages.Provider;

interface Rendu {
  source: string;
  trous: TrousDeLaCarte | undefined;
  /** Index des nœuds rendus, pour des clés React stables. */
  cle: number;
}

const MARQUES = new Set([
  'EmphasisMark',
  'CodeMark',
  'CodeInfo',
  'LinkMark',
  'HeaderMark',
  'QuoteMark',
  'ListMark',
  'URL',
  'LinkTitle',
  'LinkLabel',
  'StrikethroughMark',
  'SurlignageMarque',
  'FormuleMarque',
  'TrouMarque',
  'TrouNumero',
  'TrouIndice',
]);

const ENTITES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: '’',
  nbsp: ' ',
};

function entite(texte: string): string {
  const nom = texte.slice(1, -1);
  if (nom.startsWith('#x') || nom.startsWith('#X'))
    return String.fromCodePoint(parseInt(nom.slice(2), 16) || 0xfffd);
  if (nom.startsWith('#')) return String.fromCodePoint(parseInt(nom.slice(1), 10) || 0xfffd);
  return ENTITES[nom] ?? texte;
}

/** Lien autorisé : http(s), mailto ; tout le reste s'affiche sans lien. */
function adresseSure(url: string): string | null {
  return /^(https?:|mailto:)/i.test(url.trim()) ? url.trim() : null;
}

function enfants(node: SyntaxNode): SyntaxNode[] {
  const liste: SyntaxNode[] = [];
  for (let c = node.firstChild; c; c = c.nextSibling) liste.push(c);
  return liste;
}

function texteDe(r: Rendu, node: SyntaxNode | null | undefined): string {
  return node ? r.source.slice(node.from, node.to) : '';
}

/** Contenu en ligne de `node` entre `de` et `a` : texte des intervalles et nœuds enfants. */
function enLigne(r: Rendu, node: SyntaxNode, de = node.from, a = node.to): ReactNode[] {
  const resultat: ReactNode[] = [];
  let position = de;
  for (const enfant of enfants(node)) {
    if (enfant.to <= de || enfant.from >= a) continue;
    if (enfant.from > position) resultat.push(r.source.slice(position, enfant.from));
    if (!MARQUES.has(enfant.name)) resultat.push(rendreEnLigne(r, enfant));
    position = Math.max(position, enfant.to);
  }
  if (position < a) resultat.push(r.source.slice(position, a));
  return resultat;
}

function Formule({ latex, bloc }: { latex: string; bloc: boolean }) {
  const html = useMemo(
    () =>
      katex.renderToString(latex, {
        displayMode: bloc,
        throwOnError: false,
        trust: false,
        strict: 'ignore',
        output: 'htmlAndMathml',
      }),
    [latex, bloc],
  );
  return bloc ? (
    <div
      className="fc-formule fc-formule--bloc"
      tabIndex={0}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  ) : (
    <span className="fc-formule" dangerouslySetInnerHTML={{ __html: html }} />
  );
}

function ImageDeCarte({ chemin, alt, legende }: { chemin: string; alt: string; legende?: string }) {
  const resoudre = useContext(ContexteImages);
  const [src, setSrc] = useState<string | null>(
    /^(https?:|data:|blob:)/.test(chemin) ? chemin : null,
  );
  useEffect(() => {
    if (/^(https?:|data:|blob:)/.test(chemin)) {
      setSrc(chemin);
      return;
    }
    let actif = true;
    void resoudre(chemin)
      .then((adresse) => actif && setSrc(adresse))
      .catch(() => undefined);
    return () => {
      actif = false;
    };
  }, [chemin, resoudre]);
  const image = src ? (
    <img src={src} alt={alt} />
  ) : (
    <span className="fc-contenu__image-attente" role="img" aria-label={alt} />
  );
  if (legende === undefined) return image;
  return (
    <figure>
      {image}
      {legende && <figcaption>{legende}</figcaption>}
    </figure>
  );
}

function image(r: Rendu, node: SyntaxNode, figure: boolean): ReactNode {
  const marques = enfants(node).filter((c) => c.name === 'LinkMark');
  const alt = marques.length >= 2 ? r.source.slice(marques[0]!.to, marques[1]!.from) : '';
  const url = texteDe(r, node.getChild('URL'));
  const titre = texteDe(r, node.getChild('LinkTitle')).replace(/^["'(]|["')]$/g, '');
  return (
    <ImageDeCarte key={r.cle++} chemin={url} alt={alt} {...(figure ? { legende: titre } : {})} />
  );
}

function Trou({
  reponse,
  face,
  demande,
  indice,
}: {
  reponse: ReactNode;
  face: 'recto' | 'verso';
  demande: boolean;
  indice?: string;
}) {
  if (!demande) return <span className="fc-trou">{reponse}</span>;
  if (face === 'verso') {
    return (
      <span className="fc-trou" data-etat="revele">
        {reponse}
      </span>
    );
  }
  return (
    <span className="fc-trou" data-etat="demande">
      <span aria-hidden="true">{indice ? `[${indice}…]` : '[…]'}</span>
      <span className="fc-sr">
        <span>{indice ? `trou à compléter, indice : ${indice}` : 'trou à compléter'}</span>
      </span>
    </span>
  );
}

function rendreEnLigne(r: Rendu, node: SyntaxNode): ReactNode {
  const cle = r.cle++;
  switch (node.name) {
    case 'Emphasis':
      return <em key={cle}>{enLigne(r, node)}</em>;
    case 'StrongEmphasis':
      return <strong key={cle}>{enLigne(r, node)}</strong>;
    case 'Strikethrough':
      return <del key={cle}>{enLigne(r, node)}</del>;
    case 'InlineCode': {
      const marques = enfants(node).filter((c) => c.name === 'CodeMark');
      const de = marques[0]?.to ?? node.from;
      const a = marques[1]?.from ?? node.to;
      return <code key={cle}>{r.source.slice(de, a).replace(/^ (.+) $/, '$1')}</code>;
    }
    case 'Link': {
      const marques = enfants(node).filter((c) => c.name === 'LinkMark');
      const contenu =
        marques.length >= 2 ? enLigne(r, node, marques[0]!.to, marques[1]!.from) : enLigne(r, node);
      const href = adresseSure(texteDe(r, node.getChild('URL')));
      if (href === null) return <span key={cle}>{contenu}</span>;
      return (
        <a key={cle} href={href} target="_blank" rel="noopener noreferrer">
          {contenu}
          <span className="fc-sr"> (nouvel onglet)</span>
        </a>
      );
    }
    case 'Image':
      return image(r, node, false);
    case 'HardBreak':
      return <br key={cle} />;
    case 'Escape':
      return texteDe(r, node).slice(1);
    case 'Entity':
      return entite(texteDe(r, node));
    case 'Formule':
    case 'FormuleBloc':
      return (
        <Formule
          key={cle}
          latex={texteDe(r, node.getChild('FormuleTexte'))}
          bloc={node.name === 'FormuleBloc'}
        />
      );
    case 'Surlignage': {
      const ouvrante = texteDe(r, node.firstChild);
      const couleur = /^==\{(.+)\}$/.exec(ouvrante)?.[1];
      if (couleur?.startsWith('#')) {
        return (
          <mark
            key={cle}
            data-couleur="perso"
            style={{ ['--fc-surligne-perso' as string]: couleur }}
          >
            {enLigne(r, node)}
          </mark>
        );
      }
      return (
        <mark key={cle} data-couleur={couleur && couleur !== 'jaune' ? couleur : undefined}>
          {enLigne(r, node)}
        </mark>
      );
    }
    case 'Trou': {
      const numero = Number(texteDe(r, node.getChild('TrouNumero')).replace('::', '')) || null;
      const indice = texteDe(r, node.getChild('TrouIndice')).replace(/^::/, '').trim();
      const reponse = enLigne(r, node);
      if (r.trous === undefined) return <span key={cle}>{reponse}</span>;
      const demande = r.trous.demande === 'tous' || r.trous.demande === numero;
      return (
        <Trou
          key={cle}
          reponse={reponse}
          face={r.trous.face}
          demande={demande}
          {...(indice ? { indice } : {})}
        />
      );
    }
    default:
      // HTML et nœuds inconnus : texte brut.
      return texteDe(r, node);
  }
}

function rendreBloc(r: Rendu, node: SyntaxNode): ReactNode {
  const cle = r.cle++;
  switch (node.name) {
    case 'Paragraph': {
      const contenu = enfants(node).filter((c) => !MARQUES.has(c.name));
      // Une image seule dans son paragraphe devient une figure (légende : titre de l'image).
      if (
        contenu.length === 1 &&
        contenu[0]!.name === 'Image' &&
        texteDe(r, node).trim() === texteDe(r, contenu[0]).trim()
      ) {
        return image(r, contenu[0]!, true);
      }
      if (
        contenu.length === 1 &&
        contenu[0]!.name === 'FormuleBloc' &&
        texteDe(r, node).trim() === texteDe(r, contenu[0]).trim()
      ) {
        return rendreEnLigne(r, contenu[0]!);
      }
      return <p key={cle}>{enLigne(r, node)}</p>;
    }
    case 'ATXHeading1':
    case 'SetextHeading1':
      return <h1 key={cle}>{titre(r, node)}</h1>;
    case 'ATXHeading2':
    case 'SetextHeading2':
      return <h2 key={cle}>{titre(r, node)}</h2>;
    case 'ATXHeading3':
    case 'ATXHeading4':
    case 'ATXHeading5':
    case 'ATXHeading6':
      return <h3 key={cle}>{titre(r, node)}</h3>;
    case 'BulletList':
      return <ul key={cle}>{enfants(node).map((li) => element(r, li))}</ul>;
    case 'OrderedList': {
      const premier = node.firstChild?.getChild('ListMark');
      const debut = premier ? parseInt(texteDe(r, premier), 10) : 1;
      return (
        <ol key={cle} start={debut !== 1 && Number.isFinite(debut) ? debut : undefined}>
          {enfants(node).map((li) => element(r, li))}
        </ol>
      );
    }
    case 'Blockquote':
      return <blockquote key={cle}>{blocs(r, node)}</blockquote>;
    case 'FencedCode':
    case 'CodeBlock': {
      const info = texteDe(r, node.getChild('CodeInfo')).trim();
      const textes = enfants(node).filter((c) => c.name === 'CodeText');
      const code = textes.length > 0 ? r.source.slice(textes[0]!.from, textes.at(-1)!.to) : '';
      return <BlocDeCode key={cle} code={code} {...(info ? { langage: info } : {})} />;
    }
    case 'FormuleBloc':
      return <Formule key={cle} latex={texteDe(r, node.getChild('FormuleTexte')).trim()} bloc />;
    case 'HorizontalRule':
      return <hr key={cle} />;
    case 'HTMLBlock':
    case 'CommentBlock':
    case 'ProcessingInstructionBlock':
      return <p key={cle}>{texteDe(r, node)}</p>;
    case 'LinkReference':
      return null;
    default:
      return <p key={cle}>{texteDe(r, node)}</p>;
  }
}

/** Contenu d'un titre, sans les espaces qui entourent les `#`. */
function titre(r: Rendu, node: SyntaxNode): ReactNode[] {
  const contenu = enLigne(r, node);
  const premier = contenu[0];
  if (typeof premier === 'string') contenu[0] = premier.trimStart();
  const dernier = contenu.at(-1);
  if (typeof dernier === 'string') contenu[contenu.length - 1] = dernier.trimEnd();
  return contenu;
}

function element(r: Rendu, li: SyntaxNode): ReactNode {
  const cle = r.cle++;
  const contenu = enfants(li).filter((c) => c.name !== 'ListMark');
  if (contenu.length === 1 && contenu[0]!.name === 'Paragraph') {
    return <li key={cle}>{enLigne(r, contenu[0]!)}</li>;
  }
  return <li key={cle}>{contenu.map((c) => rendreBloc(r, c))}</li>;
}

function blocs(r: Rendu, node: SyntaxNode): ReactNode[] {
  return enfants(node)
    .filter((c) => !MARQUES.has(c.name))
    .map((c) => rendreBloc(r, c));
}

/** Rendu React d'un texte Markdown de carte (sans le conteneur `fc-contenu`). */
export function rendreMarkdown(source: string, trous?: TrousDeLaCarte): ReactNode[] {
  const arbre = (trous ? analyseurCartes : analyseurSansTrous).parse(source);
  return blocs({ source, trous, cle: 0 }, arbre.topNode);
}

/** Rendu en ligne (sans paragraphe) : question d'une carte, case de tableau. */
export function rendreEnLigneMarkdown(source: string, trous?: TrousDeLaCarte): ReactNode[] {
  const arbre = (trous ? analyseurCartes : analyseurSansTrous).parse(source);
  const r: Rendu = { source, trous, cle: 0 };
  const premier = arbre.topNode.firstChild;
  if (premier && premier.name === 'Paragraph' && premier.nextSibling === null)
    return enLigne(r, premier);
  return blocs(r, arbre.topNode);
}

export interface ContenuDeCarteProps {
  markdown: string;
  trous?: TrousDeLaCarte;
  className?: string;
  lang?: string;
}

export function ContenuDeCarte({ markdown, trous, className, lang }: ContenuDeCarteProps) {
  const rendu = useMemo(() => rendreMarkdown(markdown, trous), [markdown, trous]);
  return (
    <div className={className ? `fc-contenu ${className}` : 'fc-contenu'} lang={lang}>
      {rendu}
    </div>
  );
}
